// Enhanced AdminCoverageArea.jsx with green theme matching AdminPackages
import React, { useState, useEffect } from 'react';
import SidebarHeader from '../../components/admin/sidebar';
import { useAuth } from '../../pages/auth/AuthContext';
import RequireAdminRole from '../../components/auth/userole';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  Edit, 
  Trash2,
  MapPin,
  User,
  Phone,
  Mail,
  Map,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Plus,
  Navigation
} from 'lucide-react';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const COVERAGE_STATUS_OPTIONS = [
  { value: '', label: 'Semua Status' },
  { value: 'covered', label: 'Covered' },
  { value: 'not_covered', label: 'Not Covered' },
  { value: 'coming_soon', label: 'Coming Soon' }
];

const AdminCoverageAreaPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { user: contextUser, token: contextToken } = useAuth();
  
  const [activeTab] = useState('coverage');
  const [coverageList, setCoverageList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    name: contextUser?.fullName || contextUser?.name || 'Admin User',
    email: contextUser?.email || 'admin@telnet.co.id',
    role: contextUser?.role || 'Super Admin'
  });

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  
  // Form data for add/edit
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    province: '',
    city: '',
    district: '',
    village: '',
    postalCode: '',
    fullAddress: '',
    coverageStatus: 'not_covered',
    isActive: true,
    coordinates: {
      latitude: '',
      longitude: ''
    }
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const LIMIT = 10;

  // Enhanced auth token handling
  const authToken = contextToken ||
                    localStorage.getItem('adminToken') ||
                    localStorage.getItem('token') ||
                   sessionStorage.getItem('adminToken') ||
                   sessionStorage.getItem('token');

  // Enhanced auth headers function
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    return headers;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge color with icons
  const getStatusColor = (status) => {
    const colors = {
      covered: 'bg-green-100 text-green-800 border-green-200',
      not_covered: 'bg-red-100 text-red-800 border-red-200',
      coming_soon: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      covered: <CheckCircle size={12} className="text-green-600" />,
      not_covered: <XCircle size={12} className="text-red-600" />,
      coming_soon: <Clock size={12} className="text-yellow-600" />
    };
    return icons[status] || <MapPin size={12} className="text-gray-600" />;
  };

  // Enhanced fetch coverage areas with better error handling
  const fetchCoverageAreas = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: LIMIT.toString(),
        ...(statusFilter && { coverageStatus: statusFilter }),
        ...(search && { search })
      });

      const headers = getAuthHeaders();
      const coverageAreasUrl = `${API_BASE_URL}/api/address/admin/all?${params.toString()}`;
      
      const response = await fetch(coverageAreasUrl, {
        method: 'GET',
        headers,
        credentials: authToken ? 'include' : 'omit'
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to view coverage areas.');
        } else if (response.status === 404) {
          throw new Error('Coverage areas endpoint not found. Please check your API configuration.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}: Failed to fetch coverage areas`);
        }
      }

      const data = await response.json();

      if (data.success) {
        const areasData = Array.isArray(data.data) ? data.data : [];
        setCoverageList(areasData);
        setTotalPages(data.pagination?.totalPages || Math.ceil(areasData.length / LIMIT) || 1);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to load coverage areas');
      }
    } catch (error) {
      console.error('Error fetching coverage areas:', error);
      
      let errorMessage = error.message;
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setCoverageList([]);
      setTotalPages(1);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Update current user when context changes
  useEffect(() => {
    if (contextUser) {
      setCurrentUser({
        name: contextUser.fullName || contextUser.name || 'Admin User',
        email: contextUser.email || 'admin@telnet.co.id',
        role: contextUser.role || 'Super Admin'
      });
    }
  }, [contextUser]);

  useEffect(() => {
    fetchCoverageAreas();
  }, [page, statusFilter, authToken, search]);

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCoverageAreas();
  };

  // Form validation
  const validateForm = (data) => {
    const errors = {};
    
    if (!data.fullName.trim()) errors.fullName = 'Nama lengkap wajib diisi';
    if (!data.phone.trim()) errors.phone = 'Nomor telepon wajib diisi';
    if (!data.email.trim()) errors.email = 'Email wajib diisi';
    if (!data.province.trim()) errors.province = 'Provinsi wajib diisi';
    if (!data.city.trim()) errors.city = 'Kota wajib diisi';
    if (!data.district.trim()) errors.district = 'Distrik wajib diisi';
    if (!data.village.trim()) errors.village = 'Desa/Kelurahan wajib diisi';
    if (!data.postalCode.trim()) errors.postalCode = 'Kode pos wajib diisi';
    if (!data.fullAddress.trim()) errors.fullAddress = 'Alamat lengkap wajib diisi';
    
    // Email validation
    if (data.email && !/^\S+@\S+\.\S+$/.test(data.email)) {
      errors.email = 'Format email tidak valid';
    }
    
    // Phone validation
    if (data.phone && !/^(\+62|62|0)8[1-9][0-9]{6,9}$/.test(data.phone)) {
      errors.phone = 'Format nomor telepon tidak valid';
    }
    
    // Postal code validation
    if (data.postalCode && !/^\d{5}$/.test(data.postalCode)) {
      errors.postalCode = 'Kode pos harus 5 digit angka';
    }
    
    return errors;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      fullName: '',
      phone: '',
      email: '',
      province: '',
      city: '',
      district: '',
      village: '',
      postalCode: '',
      fullAddress: '',
      coverageStatus: 'not_covered',
      isActive: true,
      coordinates: {
        latitude: '',
        longitude: ''
      }
    });
    setFormErrors({});
  };

  // Handle add coverage area
  const handleAddCoverageArea = async (e) => {
    e.preventDefault();
    
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/address`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert('Coverage area berhasil ditambahkan!');
        setShowAddModal(false);
        resetForm();
        fetchCoverageAreas();
      } else {
        throw new Error(data.message || 'Failed to add coverage area');
      }
    } catch (error) {
      console.error('Error adding coverage area:', error);
      alert('Error menambahkan coverage area: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit coverage area
  const handleEditCoverageArea = async (e) => {
    e.preventDefault();
    
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/address/${selectedArea._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert('Coverage area berhasil diupdate!');
        setShowEditModal(false);
        setSelectedArea(null);
        resetForm();
        fetchCoverageAreas();
      } else {
        throw new Error(data.message || 'Failed to update coverage area');
      }
    } catch (error) {
      console.error('Error updating coverage area:', error);
      alert('Error mengupdate coverage area: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete coverage area
  const handleDeleteCoverageArea = async (area) => {
    if (!window.confirm(`Apakah Anda yakin ingin menghapus coverage area untuk ${area.fullName}?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/address/coverage/${area._id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert('Coverage area berhasil dihapus!');
        fetchCoverageAreas();
      } else {
        throw new Error(data.message || 'Failed to delete coverage area');
      }
    } catch (error) {
      console.error('Error deleting coverage area:', error);
      alert('Error menghapus coverage area: ' + error.message);
    }
  };

  // Open modals
  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (area) => {
    setSelectedArea(area);
    setFormData({
      fullName: area.fullName || '',
      phone: area.phone || '',
      email: area.email || '',
      province: area.province || '',
      city: area.city || '',
      district: area.district || '',
      village: area.village || '',
      postalCode: area.postalCode || '',
      fullAddress: area.fullAddress || '',
      coverageStatus: area.coverageStatus || 'not_covered',
      isActive: area.isActive !== undefined ? area.isActive : true,
      coordinates: {
        latitude: area.coordinates?.latitude || '',
        longitude: area.coordinates?.longitude || ''
      }
    });
    setShowEditModal(true);
  };

  const openDetailModal = (area) => {
    setSelectedArea(area);
    setShowDetailModal(true);
  };

  // Pagination controls
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Retry function
  const handleRetry = () => {
    fetchCoverageAreas();
  };

  // Stats Cards Component with green theme
  const StatsCards = () => {
    const stats = {
      total: coverageList.length,
      covered: coverageList.filter(area => area.coverageStatus === 'covered').length,
      not_covered: coverageList.filter(area => area.coverageStatus === 'not_covered').length,
      coming_soon: coverageList.filter(area => area.coverageStatus === 'coming_soon').length
    };

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Areas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <MapPin className="text-blue-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Covered</p>
              <p className="text-2xl font-bold text-gray-900">{stats.covered}</p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Not Covered</p>
              <p className="text-2xl font-bold text-gray-900">{stats.not_covered}</p>
            </div>
            <XCircle className="text-red-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Coming Soon</p>
              <p className="text-2xl font-bold text-gray-900">{stats.coming_soon}</p>
              <button 
                onClick={fetchCoverageAreas}
                disabled={isLoading}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center mt-1"
              >
                <RefreshCw size={12} className={`mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            <Clock className="text-yellow-500" size={32} />
          </div>
        </div>
      </div>
    );
  };

  // Mobile Header Component with green theme
  const MobileHeader = () => (
    <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Manage Coverage Areas</h1>
            <p className="text-xs text-gray-600">{currentUser.role}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {error && (
            <button
              onClick={handleRetry}
              className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 transition text-xs"
            >
              Retry
            </button>
          )}
          <div className="h-8 w-8 bg-green-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {currentUser.name.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Sidebar Overlay for Mobile
  const SidebarOverlay = () => (
    <>
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );

  // Mobile Coverage Card Component with green theme
  const MobileCoverageCard = ({ area }) => (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <button
            onClick={() => openDetailModal(area)}
            className="text-sm font-medium text-green-600 hover:text-green-800 truncate block text-left"
          >
            {area.fullName}
          </button>
          <div className="flex items-center space-x-1 text-sm text-gray-600 mb-1">
            <Phone size={12} />
            <span className="truncate">{area.phone}</span>
          </div>
          <div className="flex items-center space-x-1 text-sm text-gray-600 mb-2">
            <Mail size={12} />
            <span className="truncate">{area.email}</span>
          </div>
        </div>
        <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(area.coverageStatus)} ml-2`}>
          {getStatusIcon(area.coverageStatus)}
          <span>
            {area.coverageStatus === 'covered' ? 'Covered' : 
             area.coverageStatus === 'not_covered' ? 'Not Covered' :
             area.coverageStatus === 'coming_soon' ? 'Coming Soon' : 
             area.coverageStatus || 'Unknown'}
          </span>
        </span>
      </div>
      
      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
        <div className="flex items-center space-x-1">
          <Map size={12} className="text-gray-400" />
          <span>{area.city}</span>
        </div>
        <div>
          <span className="font-medium">Distrik:</span> {area.district}
        </div>
        <div>
          <span className="font-medium">Status:</span> 
          {area.isActive ? (
            <span className="text-green-600 font-semibold ml-1">Aktif</span>
          ) : (
            <span className="text-red-600 font-semibold ml-1">Tidak Aktif</span>
          )}
        </div>
        <div>
          <span className="font-medium">Dibuat:</span> {formatDate(area.createdAt)}
        </div>
      </div>
      
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => openDetailModal(area)}
          className="flex-1 min-w-0 text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center justify-center space-x-1"
        >
          <Eye size={12} />
          <span>Detail</span>
        </button>
        <button
          onClick={() => openEditModal(area)}
          className="flex-1 min-w-0 text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center justify-center space-x-1"
        >
          <Edit size={12} />
          <span>Edit</span>
        </button>
        <button
          onClick={() => handleDeleteCoverageArea(area)}
          className="flex-1 min-w-0 text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center justify-center space-x-1"
        >
          <Trash2 size={12} />
          <span>Hapus</span>
        </button>
      </div>
    </div>
  );

  // Desktop Table Component with green theme
  const DesktopTable = () => (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nama Lengkap</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telepon</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kota</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distrik</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status Coverage</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktif</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dibuat Pada</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {coverageList.map((area) => (
            <tr key={area._id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 cursor-pointer hover:text-green-800"
                  onClick={() => openDetailModal(area)}>
                <div className="flex items-center space-x-2">
                  <User size={16} className="text-gray-400" />
                  <span>{area.fullName}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <Phone size={14} className="text-gray-400" />
                  <span>{area.phone}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <Mail size={14} className="text-gray-400" />
                  <span>{area.email}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <Map size={14} className="text-gray-400" />
                  <span>{area.city}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{area.district}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(area.coverageStatus)}`}>
                  {getStatusIcon(area.coverageStatus)}
                  <span>
                    {area.coverageStatus === 'covered' ? 'Covered' : 
                      area.coverageStatus === 'not_covered' ? 'Not Covered' :
                      area.coverageStatus === 'coming_soon' ? 'Coming Soon' : 
                      area.coverageStatus || 'Unknown'}
                  </span>
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {area.isActive ? (
                  <span className="inline-flex items-center space-x-1 text-green-600 font-semibold">
                    <CheckCircle size={14} />
                    <span>Ya</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center space-x-1 text-red-600 font-semibold">
                    <XCircle size={14} />
                    <span>Tidak</span>
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {formatDate(area.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <div className="flex space-x-1 justify-center">
                  <button
                    onClick={() => openEditModal(area)}
                    className="text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center space-x-1"
                    title="Edit Area"
                  >
                    <Edit size={12} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => openDetailModal(area)}
                    className="text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center space-x-1"
                    title="View Details"
                  >
                    <Eye size={12} />
                    <span>Detail</span>
                  </button>
                  <button
                    onClick={() => handleDeleteCoverageArea(area)}
                    className="text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center space-x-1"
                    title="Delete Area"
                  >
                    <Trash2 size={12} />
                    <span>Hapus</span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <RequireAdminRole user={user} loading={authLoading}>
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <SidebarOverlay />
        
        {/* Sidebar - Mobile responsive */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <SidebarHeader 
            activeTab={activeTab} 
            setActiveTab={() => {}} // No tab switching needed for this page
            currentUser={currentUser}
            onClose={() => setSidebarOpen(false)}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <MobileHeader />

          <main className="flex-1 overflow-auto p-3 sm:p-6">
            {/* Main Panel dengan green theme */}
            <div className="bg-white rounded-lg shadow-xl">
              {/* Header dengan gradient hijau */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <MapPin className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h1 className="font-bold text-xl">Coverage Area Management</h1>
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="text-green-100">
                        {coverageList.length} areas ditemukan • Page {page} dari {totalPages}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={fetchCoverageAreas}
                    disabled={isLoading}
                    className="hover:bg-green-700 p-2 rounded transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                  </button>
                  <button
                    onClick={openAddModal}
                    className="bg-white text-green-600 hover:bg-green-50 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <Plus size={20} />
                    <span>Tambah Area</span>
                  </button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="p-4 border-b border-gray-200">
                <StatsCards />
              </div>

              {/* Filters and Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search */}
                  <form onSubmit={handleSearchSubmit} className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        placeholder="Cari berdasarkan nama, telepon, email, kota, atau distrik..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </form>

                  {/* Filter Toggle untuk Mobile */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Filter size={20} />
                    <span>Filter</span>
                    {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>

                  {/* Filters */}
                  <div className={`flex flex-col lg:flex-row gap-2 ${showFilters ? 'block' : 'hidden lg:flex'}`}>
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {COVERAGE_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mx-4 mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span>⚠️</span>
                      <span>{error}</span>
                    </div>
                    <button
                      onClick={handleRetry}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                    >
                      Coba Lagi
                    </button>
                  </div>
                </div>
              )}

              {/* Coverage Areas Content */}
              <div className="p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center">
                      <RefreshCw size={32} className="animate-spin text-green-600 mb-2" />
                      <span className="text-gray-500">Loading coverage areas...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center max-w-md mx-auto">
                      <div className="text-red-600 mb-4">
                        <span className="text-4xl">⚠️</span>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Coverage Areas</h3>
                      <p className="text-gray-600 mb-4">{error}</p>
                      <button
                        onClick={handleRetry}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                ) : coverageList.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <MapPin size={64} className="mx-auto opacity-30" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Coverage Areas Found</h3>
                    <p className="text-gray-600">
                      {search || statusFilter ? 'Try adjusting your search filters' : 'No coverage areas have been created yet'}
                    </p>
                    <button
                      onClick={openAddModal}
                      className="mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                    >
                      Create First Area
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Mobile Cards */}
                    <div className="sm:hidden">
                      {coverageList.map((area) => (
                        <MobileCoverageCard key={area._id} area={area} />
                      ))}
                    </div>

                    {/* Desktop Table */}
                    <div className="hidden sm:block">
                      <DesktopTable />
                    </div>
                  </>
                )}

                {/* Pagination */}
                {!error && !isLoading && coverageList.length > 0 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-3 sm:space-y-0">
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Sebelumnya
                    </button>
                    <span className="text-sm text-gray-700">
                      Halaman {page} dari {totalPages} ({coverageList.length} areas)
                    </span>
                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Berikutnya
                    </button>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>

        {/* Add Coverage Area Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Tambah Area Coverage</h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <form onSubmit={handleAddCoverageArea}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nama Lengkap *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.fullName && <p className="text-red-500 text-xs mt-1">{formErrors.fullName}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Telepon *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Provinsi *</label>
                      <input
                        type="text"
                        name="province"
                        value={formData.province}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.province && <p className="text-red-500 text-xs mt-1">{formErrors.province}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kota *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.city && <p className="text-red-500 text-xs mt-1">{formErrors.city}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Distrik *</label>
                      <input
                        type="text"
                        name="district"
                        value={formData.district}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.district && <p className="text-red-500 text-xs mt-1">{formErrors.district}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Desa/Kelurahan *</label>
                      <input
                        type="text"
                        name="village"
                        value={formData.village}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.village && <p className="text-red-500 text-xs mt-1">{formErrors.village}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kode Pos *</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        maxLength="5"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.postalCode && <p className="text-red-500 text-xs mt-1">{formErrors.postalCode}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status Coverage</label>
                      <select
                        name="coverageStatus"
                        value={formData.coverageStatus}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="not_covered">Not Covered</option>
                        <option value="covered">Covered</option>
                        <option value="coming_soon">Coming Soon</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                      <input
                        type="number"
                        name="coordinates.latitude"
                        value={formData.coordinates.latitude}
                        onChange={handleInputChange}
                        step="any"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Contoh: -6.200000"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                      <input
                        type="number"
                        name="coordinates.longitude"
                        value={formData.coordinates.longitude}
                        onChange={handleInputChange}
                        step="any"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Contoh: 106.816666"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Alamat Lengkap *</label>
                    <textarea
                      name="fullAddress"
                      value={formData.fullAddress}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                    {formErrors.fullAddress && <p className="text-red-500 text-xs mt-1">{formErrors.fullAddress}</p>}
                  </div>
                  
                  <div className="mt-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Aktif</span>
                    </label>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm sm:text-base"
                    >
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Coverage Area Modal */}
        {showEditModal && selectedArea && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Edit Area Coverage</h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <form onSubmit={handleEditCoverageArea}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nama Lengkap *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.fullName && <p className="text-red-500 text-xs mt-1">{formErrors.fullName}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Telepon *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Provinsi *</label>
                      <input
                        type="text"
                        name="province"
                        value={formData.province}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.province && <p className="text-red-500 text-xs mt-1">{formErrors.province}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kota *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.city && <p className="text-red-500 text-xs mt-1">{formErrors.city}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Distrik *</label>
                      <input
                        type="text"
                        name="district"
                        value={formData.district}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.district && <p className="text-red-500 text-xs mt-1">{formErrors.district}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Desa/Kelurahan *</label>
                      <input
                        type="text"
                        name="village"
                        value={formData.village}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.village && <p className="text-red-500 text-xs mt-1">{formErrors.village}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kode Pos *</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        maxLength="5"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                      {formErrors.postalCode && <p className="text-red-500 text-xs mt-1">{formErrors.postalCode}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status Coverage</label>
                      <select
                        name="coverageStatus"
                        value={formData.coverageStatus}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="not_covered">Not Covered</option>
                        <option value="covered">Covered</option>
                        <option value="coming_soon">Coming Soon</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                      <input
                        type="number"
                        name="coordinates.latitude"
                        value={formData.coordinates.latitude}
                        onChange={handleInputChange}
                        step="any"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Contoh: -6.200000"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                      <input
                        type="number"
                        name="coordinates.longitude"
                        value={formData.coordinates.longitude}
                        onChange={handleInputChange}
                        step="any"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Contoh: 106.816666"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Alamat Lengkap *</label>
                    <textarea
                      name="fullAddress"
                      value={formData.fullAddress}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                    {formErrors.fullAddress && <p className="text-red-500 text-xs mt-1">{formErrors.fullAddress}</p>}
                  </div>
                  
                  <div className="mt-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Aktif</span>
                    </label>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm sm:text-base"
                    >
                      {isSubmitting ? 'Updating...' : 'Update'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Detail Coverage Area Modal */}
        {showDetailModal && selectedArea && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Coverage Area Details</h3>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Personal Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <User size={18} />
                      <span>Informasi Personal</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <User size={14} className="text-gray-400" />
                        <span><span className="font-medium">Full Name:</span> {selectedArea.fullName || 'N/A'}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone size={14} className="text-gray-400" />
                        <span><span className="font-medium">Phone:</span> {selectedArea.phone || 'N/A'}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Mail size={14} className="text-gray-400" />
                        <span><span className="font-medium">Email:</span> {selectedArea.email || 'N/A'}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">Status:</span>
                        {selectedArea.isActive ? (
                          <span className="inline-flex items-center space-x-1 text-green-600 font-semibold">
                            <CheckCircle size={14} />
                            <span>Active</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center space-x-1 text-red-600 font-semibold">
                            <XCircle size={14} />
                            <span>Inactive</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Location Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Map size={18} />
                      <span>Informasi Lokasi</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Province:</span> {selectedArea.province || 'N/A'}</div>
                      <div><span className="font-medium">City:</span> {selectedArea.city || 'N/A'}</div>
                      <div><span className="font-medium">District:</span> {selectedArea.district || 'N/A'}</div>
                      <div><span className="font-medium">Village:</span> {selectedArea.village || 'N/A'}</div>
                      <div><span className="font-medium">Postal Code:</span> {selectedArea.postalCode || 'N/A'}</div>
                    </div>
                  </div>

                  {/* Coverage Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <MapPin size={18} />
                      <span>Informasi Coverage</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">Coverage Status:</span>
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(selectedArea.coverageStatus)}`}>
                          {getStatusIcon(selectedArea.coverageStatus)}
                          <span>
                            {selectedArea.coverageStatus === 'covered' ? 'Covered' : 
                              selectedArea.coverageStatus === 'not_covered' ? 'Not Covered' :
                              selectedArea.coverageStatus === 'coming_soon' ? 'Coming Soon' : 
                              selectedArea.coverageStatus || 'Unknown'}
                          </span>
                        </span>
                      </div>
                      <div><span className="font-medium">Created:</span> {formatDateTime(selectedArea.createdAt)}</div>
                      <div><span className="font-medium">Updated:</span> {formatDateTime(selectedArea.updatedAt)}</div>
                    </div>
                  </div>

                  {/* Coordinates Information */}
                  {(selectedArea.coordinates?.latitude || selectedArea.coordinates?.longitude) && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <Navigation size={18} />
                        <span>Koordinat</span>
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Latitude:</span> {selectedArea.coordinates?.latitude || 'N/A'}</div>
                        <div><span className="font-medium">Longitude:</span> {selectedArea.coordinates?.longitude || 'N/A'}</div>
                      </div>
                    </div>
                  )}

                  {/* Full Address */}
                  <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
                    <h4 className="font-semibold text-gray-900 mb-3">Alamat Lengkap</h4>
                    <div className="text-sm">
                      {selectedArea.fullAddress || 'N/A'}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      openEditModal(selectedArea);
                    }}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Edit Area
                  </button>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </RequireAdminRole>
  );
};

export default AdminCoverageAreaPage;